import { z } from "zod";
import { isSupportedCountry, parsePhoneNumberFromString, type CountryCode } from "libphonenumber-js/max";
import type { EnquiryCreate } from "../api/enquiries";

export type EnquiryField = "name" | "phoneCountry" | "phone" | "email" | "location" | "service" | "message";
export type EnquiryValues = Record<EnquiryField, string>;

function nationalPhone(values: EnquiryValues) {
  if (!isSupportedCountry(values.phoneCountry)) return undefined;
  const input = values.phone.trim();
  if (!/^[\d\s().-]+$/.test(input) || input.startsWith("00") || input.length > 30) return undefined;
  const parsed = parsePhoneNumberFromString(input, { defaultCountry: values.phoneCountry as CountryCode, extract: false });
  // Some parsers accept a bare calling code even with a default country. Require national input.
  const international = parsePhoneNumberFromString(`+${input.replace(/\D/g, "")}`);
  if (international?.isValid() && international.number === parsed?.number) return undefined;
  return parsed?.isValid() && parsed.country === values.phoneCountry ? parsed : undefined;
}

export function validateEnquiry(values: EnquiryValues): Partial<Record<EnquiryField, string>> {
  const errors: Partial<Record<EnquiryField, string>> = {};
  if (!values.name.trim() || values.name.trim().length > 150) {
    errors.name = "Enter your name using 1 to 150 characters.";
  }
  if (!isSupportedCountry(values.phoneCountry)) {
    errors.phoneCountry = "Select your phone number’s country.";
  } else if (!nationalPhone(values)) {
    errors.phone = `Enter a valid ${values.phoneCountry} phone number without the country code.`;
  }
  const email = values.email.trim();
  if (email && !z.string().email().max(254).safeParse(email).success) {
    errors.email = "Enter a valid email address, such as name@example.com.";
  }
  if (!values.location.trim() || values.location.trim().length > 500) {
    errors.location = "Enter the care location using 1 to 500 characters.";
  }
  if (!values.service.trim() || values.service.trim().length > 200) {
    errors.service = "Choose a service type.";
  }
  if (values.message.trim().length > 5000) errors.message = "Keep your notes within 5,000 characters.";
  return errors;
}

export function buildEnquiryPayload(values: EnquiryValues): EnquiryCreate {
  const phone = nationalPhone(values);
  if (!phone) throw new Error("Enter a valid national phone number for the selected country.");
  return {
    name: values.name.trim(),
    phone_no: phone.number,
    email: values.email.trim() || null,
    location: values.location.trim(),
    service: values.service.trim(),
    note: values.message.trim() || null,
  };
}
